import React from 'react'
import { List, Badge, Typography, Empty, theme } from 'antd'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/de'
import { logger } from '../../lib/logger'
import type { IntercomConversationListProps } from './IntercomConversationList.types'

dayjs.extend(relativeTime)

export function IntercomConversationList({
  conversations,
  selectedConversationId,
  onSelectConversation,
}: IntercomConversationListProps): React.JSX.Element {
  const { token } = theme.useToken()
  const { t } = useTranslation()

  if (conversations.length === 0) {
    return <Empty description={t('frontend:intercom.noConversationsFound')} />
  }

  return (
    <List
      dataSource={conversations}
      renderItem={(conversation) => {
        // Debug logging for each conversation
        if (import.meta.env.MODE === 'development') {
          logger.debug('Rendering conversation', {
            id: conversation.id,
            company: conversation.company,
            primary_contact: conversation.primary_contact,
            initial_message_author: conversation.initial_message_author,
            last_message_author: conversation.last_message_author,
            fullConversation: conversation,
          })
        }

        const isSelected = conversation.id === selectedConversationId
        const stateColor =
          conversation.state === 'open'
            ? 'blue'
            : conversation.state === 'snoozed'
              ? 'orange'
              : 'default'

        // Extract selection logic to be reusable for both click and keyboard events
        const handleSelectConversation = () => {
          const conversationId = conversation.id
          if (
            conversationId &&
            typeof conversationId === 'string' &&
            conversationId.trim() !== ''
          ) {
            onSelectConversation(conversationId.trim())
          } else {
            logger.error(
              'Invalid conversation ID in IntercomConversationList',
              {
                id: conversationId,
                type: typeof conversationId,
                conversationId: conversation.id,
              }
            )
          }
        }

        // Handle keyboard events for accessibility
        const handleKeyDown = (event: React.KeyboardEvent) => {
          if (event.key === 'Enter' || event.key === ' ') {
            if (event.key === ' ') {
              event.preventDefault()
            }
            handleSelectConversation()
          }
        }

        return (
          <List.Item
            role="button"
            tabIndex={0}
            style={{
              cursor: 'pointer',
              backgroundColor: isSelected
                ? token.colorFillTertiary
                : 'transparent',
              padding: `${token.paddingXS}px ${token.paddingSM}px`,
            }}
            onClick={handleSelectConversation}
            onKeyDown={handleKeyDown}
          >
            <List.Item.Meta
              title={
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: token.marginXS,
                    flexWrap: 'wrap',
                  }}
                >
                  <Badge color={stateColor} />
                  <Typography.Text
                    strong={isSelected}
                    style={{ fontSize: token.fontSizeSM }}
                  >
                    {/* Show initial message sender + company name + email if available */}
                    {(() => {
                      // Prioritize initial message author (person who started the conversation)
                      const contactName =
                        conversation.initial_message_author?.name ||
                        conversation.primary_contact?.name ||
                        conversation.last_message_author?.name
                      const contactEmail =
                        conversation.initial_message_author?.email ||
                        conversation.primary_contact?.email ||
                        conversation.last_message_author?.email
                      const companyName = conversation.company?.name

                      // Build display string
                      let displayName =
                        contactName ||
                        contactEmail ||
                        t('frontend:intercom.unknown')

                      // Add email in parentheses if we have name and email
                      if (contactName && contactEmail) {
                        displayName = `${contactName} (${contactEmail})`
                      }

                      // Add company name if available
                      if (companyName) {
                        return `${displayName} - ${companyName}`
                      }
                      return displayName
                    })()}
                  </Typography.Text>
                </div>
              }
              description={
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: token.marginXXS,
                    flexWrap: 'wrap',
                  }}
                >
                  {(() => {
                    // Use last_message_at if available, otherwise fall back to updated_at
                    // Intercom API returns null for last_message_at on email-based conversations
                    const timestamp =
                      conversation.last_message_at || conversation.updated_at

                    if (!timestamp) {
                      return (
                        <Typography.Text
                          type="secondary"
                          style={{ fontSize: token.fontSizeSM - 1 }}
                        >
                          {t('frontend:intercom.noMessages')}
                        </Typography.Text>
                      )
                    }

                    return (
                      <>
                        <Typography.Text
                          type="secondary"
                          style={{ fontSize: token.fontSizeSM - 1 }}
                        >
                          {dayjs(timestamp * 1000).fromNow()}
                        </Typography.Text>
                        {conversation.last_message_author?.type === 'admin' && (
                          <Badge
                            dot
                            color="blue"
                            style={{ marginLeft: token.marginXXS }}
                          />
                        )}
                      </>
                    )
                  })()}
                </div>
              }
            />
          </List.Item>
        )
      }}
    />
  )
}
